import { storage } from './firebase';
import { ref, uploadBytesResumable, getDownloadURL, getBlob, deleteObject } from 'firebase/storage';

/**
 * Sube un video a Firebase Storage
 */
export const uploadVideo = (file, onProgress) => {
    return new Promise((resolve, reject) => {
        if (!file) {
            reject("No se proporcionó archivo");
            return;
        }

        // Crear nombre seguro para el archivo
        const safeName = file.name.replace(/[^a-z0-9.]/gi, '_').toLowerCase();
        const fileName = `videos/${Date.now()}_${safeName}`;
        const storageRef = ref(storage, fileName);

        console.log("[Storage] 🚀 Iniciando subida:", fileName, "Tamaño:", (file.size / (1024 * 1024)).toFixed(2), "MB");

        const uploadTask = uploadBytesResumable(storageRef, file);

        let isFinished = false;
        let watchdogTimer = null;

        const finalizeSuccess = async (snapshot) => {
            if (isFinished) return;
            isFinished = true;
            if (watchdogTimer) clearTimeout(watchdogTimer);

            console.log("[Storage] ✅ Subida completada. Obteniendo URL...");

            try {
                // Obtener URL de descarga
                const url = await getDownloadURL(storageRef);
                console.log("[Storage] ✅ URL obtenida:", url.substring(0, 100) + "...");
                resolve(url);
            } catch (error) {
                console.error("[Storage] ❌ Error obteniendo URL:", error);
                reject(error);
            }
        };

        // 1. OBSERVER de progreso
        uploadTask.on('state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                console.log(`[Storage] 📊 Progreso: ${progress.toFixed(2)}% | Estado: ${snapshot.state}`);

                if (onProgress) onProgress(progress);

                // Watchdog para evitar que se quede atascado en 100%
                if (progress >= 99.9 && !isFinished && !watchdogTimer) {
                    console.log("[Storage] ⚠️ 100% detectado. Activando watchdog...");
                    watchdogTimer = setTimeout(() => {
                        if (!isFinished) {
                            console.warn("[Storage] ⏰ Watchdog: Forzando finalización");
                            finalizeSuccess(uploadTask.snapshot);
                        }
                    }, 3000);
                }
            },
            (error) => {
                if (watchdogTimer) clearTimeout(watchdogTimer);
                if (!isFinished) {
                    console.error("[Storage] ❌ Error en subida:", error);
                    reject(error);
                }
            },
            () => {
                console.log("[Storage] ✅ Observer reporta éxito");
                finalizeSuccess(uploadTask.snapshot);
            }
        );

        // 2. PROMESA (como respaldo)
        uploadTask.then((snapshot) => {
            console.log("[Storage] ✅ Promesa completada");
            finalizeSuccess(snapshot);
        }).catch((err) => {
            if (!isFinished) {
                console.error("[Storage] ❌ Error en promesa:", err);
                reject(err);
            }
        });
    });
};

/**
 * Descarga un video desde Firebase Storage
 */
export const downloadVideo = async (videoUrl) => {
    try {
        console.log("[Storage] ⬇️ Intentando descarga SDK:", videoUrl);

        // Crear referencia desde la URL
        const storageRef = ref(storage, videoUrl);
        const blob = await getBlob(storageRef);

        console.log("[Storage] ✅ Descarga exitosa. Tamaño:", (blob.size / (1024 * 1024)).toFixed(2), "MB");
        return blob;
    } catch (error) {
        console.error("[Storage] ❌ Error en descarga SDK:", error);
        throw error;
    }
};

/**
 * Elimina un archivo de video de Firebase Storage
 */
export const deleteVideoFile = async (videoUrl) => {
    try {
        if (!videoUrl) {
            console.warn("[Storage] ⚠️ No hay URL para eliminar");
            return;
        }

        console.log("[Storage] 🗑️ Intentando eliminar archivo:", videoUrl);
        const storageRef = ref(storage, videoUrl);
        await deleteObject(storageRef);
        console.log("[Storage] ✅ Archivo eliminado exitosamente");
    } catch (error) {
        console.error("[Storage] ❌ Error eliminando archivo:", error);
        throw error;
    }
};

/**
 * Copia un video existente a una nueva ubicación en Firebase Storage
 * Esto es necesario para duplicados para que no mueran cuando se elimina el original
 */
export const copyVideo = async (sourceUrl, newFileName) => {
    try {
        console.log("[Storage] 📋 Copiando video:", sourceUrl);

        // 1. Descargar el video original
        const sourceRef = ref(storage, sourceUrl);
        const blob = await getBlob(sourceRef);

        console.log("[Storage] ✅ Video descargado. Tamaño:", (blob.size / (1024 * 1024)).toFixed(2), "MB");

        // 2. Crear nuevo nombre de archivo
        const fileName = `videos/${Date.now()}_${newFileName}`;
        const destRef = ref(storage, fileName);

        // 3. Subir a nueva ubicación
        console.log("[Storage] ⬆️ Subiendo copia a:", fileName);
        await uploadBytesResumable(destRef, blob);

        // 4. Obtener URL de la copia
        const newUrl = await getDownloadURL(destRef);
        console.log("[Storage] ✅ Copia completada. Nueva URL:", newUrl.substring(0, 100) + "...");

        return newUrl;
    } catch (error) {
        console.error("[Storage] ❌ Error copiando video:", error);
        throw error;
    }
};